import React from "react";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";

export const MENTION_TRANSCRIPT_CONTEXT_PAD_SEC = 20;

function formatSecondsAsHms(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

export function formatTranscriptSegmentLabel(start: number, end: number): string {
  return `[${formatSecondsAsHms(start)}–${formatSecondsAsHms(end)}](${Math.round(start)}–${Math.round(end)})`;
}

export type TranscriptExcerptSegment = {
  start: number;
  end: number;
  text: string;
  words?: Array<{ word: string; start: number; end: number }>;
};

export type TranscriptSearchMatch = {
  segmentIndex: number;
  matchIndexInSegment: number;
  matchKey: string;
};

export type TranscriptSearchHighlightOptions = {
  searchQuery: string;
  activeMatchKey: string | null;
  segmentIndex: number;
};

export function getTranscriptSearchMatchKey(
  segmentIndex: number,
  matchIndexInSegment: number,
): string {
  return `${segmentIndex}:${matchIndexInSegment}`;
}

export function findTranscriptSearchMatches(
  segments: TranscriptExcerptSegment[],
  query: string,
): TranscriptSearchMatch[] {
  const trimmed = query.trim();
  if (!trimmed) return [];

  const needle = trimmed.toLowerCase();
  const matches: TranscriptSearchMatch[] = [];

  segments.forEach((seg, segmentIndex) => {
    const haystack = seg.text.replace(/\s+/g, " ").trim().toLowerCase();
    let startAt = 0;
    let matchIndexInSegment = 0;

    while (startAt < haystack.length) {
      const idx = haystack.indexOf(needle, startAt);
      if (idx === -1) break;

      matches.push({
        segmentIndex,
        matchIndexInSegment,
        matchKey: getTranscriptSearchMatchKey(segmentIndex, matchIndexInSegment),
      });
      matchIndexInSegment += 1;
      startAt = idx + needle.length;
    }
  });

  return matches;
}

export function findTranscriptSegmentIndexForTime(
  segments: TranscriptExcerptSegment[],
  timeSec: number,
): number {
  if (segments.length === 0) return 0;

  for (let i = 0; i < segments.length; i++) {
    if (segments[i]!.end > timeSec) return i;
  }

  return segments.length - 1;
}

function wordInMentionWindow(
  word: { start: number; end: number },
  windowMin: number,
  windowMax: number,
): boolean {
  return word.end > windowMin && word.start < windowMax;
}

function segmentInMentionWindow(
  seg: { start: number; end: number },
  windowMin: number,
  windowMax: number,
): boolean {
  return seg.end > windowMin && seg.start < windowMax;
}

function renderSegmentTextWithSearchHighlight(
  seg: TranscriptExcerptSegment,
  mentionStart: number | null,
  mentionEnd: number | null,
  searchOptions: TranscriptSearchHighlightOptions,
): React.ReactNode {
  const text = seg.text.replace(/\s+/g, " ").trim();
  const hasMentionWindow = mentionStart != null && mentionEnd != null;
  const inMention =
    hasMentionWindow && segmentInMentionWindow(seg, mentionStart, mentionEnd);
  const baseSx = !hasMentionWindow
    ? {}
    : inMention
      ? { fontStyle: "italic" as const }
      : { color: "text.disabled" as const };
  const query = searchOptions.searchQuery.trim();
  const needle = query.toLowerCase();
  const haystack = text.toLowerCase();
  const parts: React.ReactNode[] = [];
  let startAt = 0;
  let matchIndexInSegment = 0;

  while (startAt <= text.length) {
    const idx = haystack.indexOf(needle, startAt);
    if (idx === -1) {
      if (startAt < text.length) {
        parts.push(
          <Box component="span" sx={baseSx} key={`tail-${startAt}`}>
            {text.slice(startAt)}
          </Box>,
        );
      }
      break;
    }

    if (idx > startAt) {
      parts.push(
        <Box component="span" sx={baseSx} key={`pre-${startAt}`}>
          {text.slice(startAt, idx)}
        </Box>,
      );
    }

    const matchKey = getTranscriptSearchMatchKey(
      searchOptions.segmentIndex,
      matchIndexInSegment,
    );
    const isActive = matchKey === searchOptions.activeMatchKey;
    parts.push(
      <Box
        component="span"
        key={`match-${idx}-${matchIndexInSegment}`}
        sx={{
          ...baseSx,
          bgcolor: (theme) =>
            isActive
              ? alpha(theme.palette.warning.main, 0.55)
              : alpha(theme.palette.warning.main, 0.25),
        }}
      >
        {text.slice(idx, idx + query.length)}
      </Box>,
    );
    matchIndexInSegment += 1;
    startAt = idx + query.length;
  }

  return (
    <Box component="span" sx={{ whiteSpace: "normal" }}>
      {parts}
    </Box>
  );
}

export function renderSegmentTranscriptText(
  seg: TranscriptExcerptSegment,
  mentionStart: number | null,
  mentionEnd: number | null,
  searchOptions?: TranscriptSearchHighlightOptions,
): React.ReactNode {
  if (searchOptions?.searchQuery.trim()) {
    return renderSegmentTextWithSearchHighlight(
      seg,
      mentionStart,
      mentionEnd,
      searchOptions,
    );
  }

  const mentionSx = { fontStyle: "italic" } as const;
  const contextSx = { color: "text.disabled" } as const;
  const normalizedText = seg.text.replace(/\s+/g, " ").trim();

  // No active mention: render plain transcript text.
  if (mentionStart == null || mentionEnd == null) {
    return <Box component="span">{normalizedText}</Box>;
  }

  const windowStart = mentionStart;
  const windowEnd = mentionEnd;
  const inMention = segmentInMentionWindow(seg, windowStart, windowEnd);

  // Outside the mention window: one node (full episodes can be thousands of words).
  if (!inMention) {
    return (
      <Box component="span" sx={contextSx}>
        {normalizedText}
      </Box>
    );
  }

  // Fully inside: segment-level italic is enough.
  const fullyInside = seg.start >= windowStart && seg.end <= windowEnd;
  if (fullyInside || !seg.words?.length) {
    return (
      <Box component="span" sx={mentionSx}>
        {normalizedText}
      </Box>
    );
  }

  // Partial overlap only: word-level so the italic boundary is precise.
  const trimmedWords = seg.words
    .map((w) => ({ ...w, text: w.word.trim() }))
    .filter((w) => w.text.length > 0);

  return (
    <Box component="span" sx={{ whiteSpace: "normal" }}>
      {trimmedWords.map((w, wordIndex) => (
        <Box
          component="span"
          key={`${w.start}-${wordIndex}`}
          sx={wordInMentionWindow(w, windowStart, windowEnd) ? mentionSx : contextSx}
        >
          {wordIndex > 0 ? " " : ""}
          {w.text}
        </Box>
      ))}
    </Box>
  );
}

export function formatMentionTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
}
